import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
export const BACKEND = BACKEND_URL;

/**
 * Extract a human-readable message from an axios error.
 * FastAPI returns validation errors as an array of {loc,msg,type} objects
 * — passing that directly to a React toast crashes with "Objects are not
 * valid as a React child". Always normalize through this helper.
 */
export function errMsg(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === "string" ? d : d.msg || JSON.stringify(d))).join("; ");
  }
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return err?.message || fallback;
}

export function getCookie(name) {
  if (typeof document === "undefined") return "";
  const escaped = name.replace(/[$()*+./?[\\\]^{|}-]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

export function csrfHeaders() {
  const token = getCookie("csrftoken");
  return token ? { "X-CSRF-Token": token } : {};
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const csrf = getCookie("csrftoken");
  if (csrf) config.headers["X-CSRF-Token"] = csrf;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // Non-blocking – pages using auth will redirect via /auth/me check
    }
    return Promise.reject(err);
  }
);

export default api;

export const fileUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${BACKEND}${path}`;
};

/**
 * A stored file's identity: its URL without the ?exp=&sig= (or MinIO presign)
 * query, which changes from one read to the next. Use it to compare or key by
 * file URL -- screenshot annotations -- never to load a file, which needs the
 * query. The backend normalizes whatever form it is sent.
 */
export const fileKey = (url) => (url || "").split("?")[0];

/** Re-key a map keyed by file URL (e.g. screenshot_annotations) by fileKey. */
export const keyByFile = (map) =>
  Object.fromEntries(Object.entries(map || {}).map(([k, v]) => [fileKey(k), v]));
